import { fileExtension, isCompanionOnlyExtension } from './modelPackagePlan';
import { getFileInfoFromName } from '../../../utils/fileTypeMap';
import { validateModelPackage } from './validateModelPackage';
import { scanObj } from './scanObj';

const MAX_TEXT = 32 * 1024 * 1024;
const readText = file => typeof file.text === 'function' ? file.text() : new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsText(file); });
// Browser File names and webkitRelativePath values are literal filenames, not
// URI strings. Decode only URI-encoded glTF references; OBJ/MTL paths are literal.
function safePath(value, { encodedUri = false } = {}) {
  if (typeof value !== 'string') throw new Error('Use a valid relative package path.');
  let decoded = value;
  if (encodedUri) {
    try { decoded = decodeURIComponent(value); }
    catch { throw new Error('Model reference has invalid URI encoding.'); }
  }
  const path = decoded.replace(/\\/g, '/').replace(/^\.\//, '');
  if (!path || path.startsWith('/') || path.includes(':') || Array.from(path).some(character => character.charCodeAt(0) < 32) || path.split('/').some(part => !part || part === '..' || part === '.')) throw new Error('Use safe relative package paths.');
  return path;
}

async function references(file) {
  const ext = fileExtension(file.name);
  if (!['gltf', 'obj', 'mtl'].includes(ext)) return [];
  if (ext === 'obj') return (await scanObj(file)).references;
  if (file.size > (ext === 'mtl' ? 4 * 1024 * 1024 : MAX_TEXT)) throw new Error('Package text exceeds its safe parsing limit.');
  const text = await readText(file);
  if (ext === 'gltf') {
    const json = JSON.parse(text);
    return [...(json.buffers || []), ...(json.images || [])].map(item => item.uri).filter(uri => uri && !uri.startsWith('data:'));
  }
  return text.split(/\r?\n/).flatMap(line => {
    const match = line.trim().match(ext === 'obj' ? /^mtllib\s+(.+)$/i : /^(?:map_\w+|bump|disp|decal|norm)\s+(.+)$/i);
    if (!match) return [];
    // Option-bearing MTL paths are ambiguous. Never guess which file to attach.
    if (match[1].startsWith('-')) throw new Error('Use an MTL with explicit texture paths without map options.');
    return [match[1].trim()];
  });
}

/** Plans independent roots using explicit glTF/OBJ/MTL references, never extension proximity.
 * Files remain browser-owned; this result is coordination, not another Asset store.
 */
export async function planAssetIntake(selection) {
  const files = Array.from(selection || []);
  if (!files.length || files.length > 100 * 201) throw new Error('Choose files for at most 100 logical assets.');
  const paths = new Map(), duplicates = new Set();
  for (const file of files) {
    const path = safePath(file.webkitRelativePath || file.name).toLowerCase();
    if (paths.has(path)) duplicates.add(path);
    else paths.set(path, file);
  }
  const consumed = new Set(), plans = [];
  // Model roots claim references before images become independent roots.
  for (const file of files.filter(file => ['gltf', 'obj', 'fbx', 'glb'].includes(fileExtension(file.name)))) {
    const companions = [], visited = new Set([file]);
    let error = ''; const warnings = [];
    try {
      async function visit(source) {
        const sourcePath = safePath(source.webkitRelativePath || source.name);
        const base = sourcePath.includes('/') ? sourcePath.slice(0, sourcePath.lastIndexOf('/') + 1) : '';
        for (const reference of await references(source)) {
          const referencePath = safePath(reference, { encodedUri: fileExtension(source.name) === 'gltf' });
          if (referencePath.includes('/')) throw new Error('Nested package paths are not supported by this upload transport. Export a flat package with unique basenames and updated references, or a self-contained GLB.');
          const path = safePath(base + referencePath).toLowerCase();
          if (duplicates.has(path)) throw new Error('Ambiguous package paths. Select one file for each relative path.');
          const companion = paths.get(path);
          if (!companion) {
            if (fileExtension(source.name) === 'obj' && fileExtension(reference) === 'mtl') { warnings.push(`Missing ${reference}. Geometry will open with default materials; original source is preserved.`); continue; }
            throw new Error(`Missing package companion: ${reference}. Select the complete package again.`);
          }
          if (visited.has(companion)) continue;
          if (['gltf', 'obj', 'fbx', 'glb'].includes(fileExtension(companion.name))) throw new Error('A model root cannot be another model’s companion.');
          visited.add(companion); companions.push(companion); consumed.add(companion);
          if (companions.length > 200) throw new Error('A model package supports at most 200 companions.');
          await visit(companion);
        }
      }
      await visit(file);
      await validateModelPackage(file, companions);
    } catch (failure) { error = failure.message || 'Invalid package.'; }
    plans.push({ rootFile: file, companions, error, warnings });
  }
  for (const file of files) {
    if (consumed.has(file) || plans.some(plan => plan.rootFile === file)) continue;
    plans.push({ rootFile: file, companions: [], error: isCompanionOnlyExtension(file.name) ? 'Orphan companion. Include the model that references this file.' : '' });
  }
  if (plans.length > 100) throw new Error('Import at most 100 logical assets in one selection.');
  return plans.map((plan, index) => {
    const info = getFileInfoFromName(plan.rootFile.name);
    const path = safePath(plan.rootFile.webkitRelativePath || plan.rootFile.name).toLowerCase();
    let error = plan.error;
    if (duplicates.has(path)) error = 'Duplicate path in selection. Choose each source once.';
    if (!info || info.capability === 'REJECTED') error = 'Unsupported source format. Export a supported format and choose it again.';
    if (!Number.isSafeInteger(plan.rootFile.size) || plan.rootFile.size <= 0 || plan.rootFile.size > 5 * 1024 ** 3) error = 'Use a nonempty file within the 5 GiB safety ceiling and your plan limits.';
    return { ...plan, error, fileInfo: info, clientItemId: String(index + 1), state: error ? 'rejected' : 'validating' };
  });
}
